/**
 * Market labels for order / fill rows.
 *   "#<enc>"  → outcome: id + side name from outcomeMeta sideSpecs (via outcomes.ts, the only id code)
 *   "@<n>"    → spot pair by spotMeta universe index ("HYPE/USDC")
 *   anything else (BTC, xyz:TSLA, PURR/USDC) → shown as-is
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hlQueryKey, useHlEnv, useInfoClient } from '../../hl/react';
import { useOutcomeMetaForNames } from '../../catalog/useOutcomeMeta';
import { fromCoin, type Side } from '../../hl/outcomes';
import { describeOutcome, snapshotRegistry, type TemplateRegistry } from '../../describe';
import { useOutcomeTemplates } from '../../describe/useDescribe';
import type { OutcomeMetaResponse, OutcomeSpec, SpotMetaResponse } from '../../hl/types';
import { cents, localizeNumber } from '../../i18n/format';

export type MarketKind = 'outcome' | 'spot' | 'perp';

export interface MarketLabel {
  kind: MarketKind;
  /** Primary text, e.g. "#13103 Yes", "HYPE/USDC", "BTC". */
  label: string;
  /** Secondary text (human outcome title from describe/), if any. */
  title?: string;
  /** Outcome side, for colouring. */
  side?: Side;
  /** Outcome side label ("Yes", "Ravens", "Over"). */
  sideName?: string;
  outcome?: number;
}

export interface LabelIndex {
  outcomes: Map<number, OutcomeSpec>;
  spotNames: Map<number, string>;
  /** Full outcomeMeta (question titles) and template formats for describe/. */
  meta?: OutcomeMetaResponse;
  templates?: TemplateRegistry;
}

export const EMPTY_INDEX: LabelIndex = { outcomes: new Map(), spotNames: new Map() };

export function buildLabelIndex(
  outcomeMeta?: OutcomeMetaResponse,
  spotMeta?: SpotMetaResponse,
  templates?: TemplateRegistry,
): LabelIndex {
  const outcomes = new Map<number, OutcomeSpec>();
  for (const o of outcomeMeta?.outcomes ?? []) outcomes.set(o.outcome, o);
  const spotNames = new Map<number, string>();
  if (spotMeta) {
    const tokenName = new Map<number, string>();
    for (const t of spotMeta.tokens) tokenName.set(t.index, t.name);
    for (const u of spotMeta.universe) {
      const pair = `${tokenName.get(u.tokens[0]) ?? '?'}/${tokenName.get(u.tokens[1]) ?? '?'}`;
      spotNames.set(u.index, u.name.startsWith('@') ? pair : u.name);
    }
  }
  return { outcomes, spotNames, meta: outcomeMeta, templates };
}

export function labelCoin(coin: string, index: LabelIndex): MarketLabel {
  const ref = fromCoin(coin);
  if (ref) {
    const meta = index.outcomes.get(ref.outcome);
    const desc = meta ? describeOutcome(meta, index.meta, { templates: index.templates ?? snapshotRegistry() }) : undefined;
    const name = desc ? desc.sideLabels[ref.side] : ref.side === 0 ? 'Yes' : 'No';
    return {
      kind: 'outcome',
      label: `#${ref.outcome} ${name}`,
      title: desc?.title,
      side: ref.side,
      sideName: name,
      outcome: ref.outcome,
    };
  }
  const spot = /^@(\d+)$/.exec(coin);
  if (spot) {
    const n = Number(spot[1]);
    return { kind: 'spot', label: index.spotNames.get(n) ?? coin };
  }
  if (coin.includes('/')) return { kind: 'spot', label: coin };
  return { kind: 'perp', label: coin };
}

export function isOutcomeCoin(coin: string): boolean {
  return fromCoin(coin) !== null;
}

/** Toast / one-line label: "Ravens · Baltimore Ravens vs Cleveland Browns" for outcomes, the plain label otherwise. */
export function readableLabel(coin: string, index: LabelIndex): string {
  const l = labelCoin(coin, index);
  return l.kind === 'outcome' && l.title ? `${l.sideName} · ${l.title}` : l.label;
}

/** outcomeMeta + spotMeta (cached for the session, keyed per env) → label index. */
export function useLabelIndex(): LabelIndex {
  const env = useHlEnv();
  const info = useInfoClient();
  // The live catalog plus what just left it: a settlement fill's toast comes after its outcomeSettled.
  const om = useOutcomeMetaForNames();
  const sm = useQuery({
    queryKey: hlQueryKey(env, 'spotMeta'),
    queryFn: ({ signal }) => info.spotMeta({ signal }),
    staleTime: 10 * 60_000,
  });
  const templates = useOutcomeTemplates();
  return useMemo(
    () => (om || sm.data ? buildLabelIndex(om, sm.data, templates) : EMPTY_INDEX),
    [om, sm.data, templates],
  );
}

/** Price shown in cents for outcomes ("39.583¢"), plain for other markets. */
export function formatPx(px: string, kind: MarketKind): string {
  if (kind !== 'outcome') return localizeNumber(stripZeros(px));
  const n = Number(px) * 100;
  if (!Number.isFinite(n)) return px;
  return cents(stripZeros(n.toFixed(3)));
}

export function stripZeros(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/0+$/, '').replace(/\.$/, '');
}
